import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { FaLinkedin, FaGithub, FaEnvelope, FaDownload } from 'react-icons/fa';
import TypingAnimation from './components/TypingAnimation';
import RotatingEarth from './components/RotatingEarth';
import Map from './components/Map';
import './App.css';

interface AppContainerProps {
  showEarth: boolean;
}

const AppContainer = styled.div<AppContainerProps>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.showEarth ? '#F5F3EF' : '#000'};
  color: ${props => props.showEarth ? '#3E3E3E' : 'white'};
  overflow: hidden;
  transition: background-color 0.5s ease, color 0.5s ease;
`;

const MainContent = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  position: relative;
  align-items: center;
  justify-content: center;
`;

const LeftSection = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const RightSection = styled.div`
  position: absolute;
  left: 60%;
  top: 40%;
  transform: translate(-50%, -50%);
  z-index: 20;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const IntroText = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 2rem;
  line-height: 1.8;
  color: #3E3E3E;
  max-width: 500px;
  text-align: left;
  
  @media (max-width: 1024px) {
    font-size: 1.6rem;
  }
`;

const TextContainer = styled.div`
  font-size: 3rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 20%;
  width: 100%;
  position: absolute;
  top: 35%;
  transform: translateY(-50%);
  z-index: 10;
  color: #3E3E3E;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    padding-left: 10%;
  }
`;

const ContactSection = styled.div`
  margin-top: 4rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2rem;
    left: 0;
    width: 60px;
    height: 1px;
    background-color: rgba(139, 92, 246, 0.3);
  }
`;

const ContactLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  align-items: flex-start;
`;

const ContactLink = styled.a`
  color: #3E3E3E;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.5rem 0;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  white-space: nowrap;
  
  &:hover {
    color: #8B5CF6;
    border-bottom-color: #8B5CF6;
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;

const HomePage: React.FC = () => {
  const [showEarth, setShowEarth] = useState(false);
  
  return (
    <AppContainer showEarth={showEarth}>
      {!showEarth ? (
        <TypingAnimation onComplete={() => setShowEarth(true)} />
      ) : (
        <MainContent>
          <LeftSection>
            <TextContainer>Hello </TextContainer>
            <RotatingEarth />
          </LeftSection>
          <RightSection>
            <IntroText>
              <span style={{ fontSize: '2.4rem', fontWeight: '600', color: '#3E3E3E', fontStyle: 'italic' }}>I'm Praveenaa.</span> <br />
              <span style={{ fontStyle: 'italic' }}>I work with <span style={{ textDecoration: 'underline', textDecorationColor: '#8B5CF6' }}>geospatial data</span> to reveal insights and build tools.</span> <br />
              <span style={{ fontStyle: 'italic' }}>My focus is climate data that helps us understand our changing planet.</span>
            </IntroText>
            <ContactSection>
              <ContactLinks>
                <ContactLink href="https://www.linkedin.com/in/praveenaa-kulandhaivel/" target="_blank" rel="noopener noreferrer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </ContactLink>
                <ContactLink href="https://github.com/praveenaak" target="_blank" rel="noopener noreferrer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  GitHub
                </ContactLink>
                <ContactLink href="mailto:praveenaa2408@gmail.com">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  praveenaa2408@gmail.com
                </ContactLink>
              </ContactLinks>
              <ContactLinks>
                <ContactLink href="/PraveenaaKulandhaivel.pdf" download="PraveenaaKulandhaivel.pdf">
                  Resume 
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                </ContactLink>
              </ContactLinks>
            </ContactSection>
          </RightSection>
        </MainContent>
      )}
    </AppContainer>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/map" element={<Map />} />
      </Routes>
    </Router>
  );
}

export default App;